// Firebase Storage utility for drawing uploads
// Initialise Firebase using the config object from your .env / environment
// In Create-React-App use REACT_APP_ prefix for env vars

import { initializeApp, getApps } from "firebase/app";
import {
  getStorage,
  ref,
  uploadBytes,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";

// ─── Config ──────────────────────────────────────────────────────────────────
// Fill these values in  frontend/.env  (create it from .env.example)
const firebaseConfig = {
  apiKey:            process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain:        process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId:         process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket:     process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId:             process.env.REACT_APP_FIREBASE_APP_ID,
};

// Initialise once
const app = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);
const storage = getStorage(app);

// ─── Upload ───────────────────────────────────────────────────────────────────
/**
 * Upload a drawing file to Firebase Storage.
 *
 * Storage path: hiland-surveyors/{adminId}/sites/{siteId}/drawings/{timestamp}_{filename}
 *
 * @param {File}     file
 * @param {string}   adminId
 * @param {string}   siteId
 * @param {Function} onProgress  – called with (0-100) percent
 * @returns {Promise<{ firebasePath: string, firebaseURL: string }>}
 */
export const uploadDrawingFile = (file, adminId, siteId, onProgress) => {
  return new Promise((resolve, reject) => {
    const timestamp  = Date.now();
    const safeName   = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
    const path       = `hiland-surveyors/${adminId}/sites/${siteId}/drawings/${timestamp}_${safeName}`;
    const storageRef = ref(storage, path);

    const task = uploadBytesResumable(storageRef, file, {
      contentType: file.type || "application/octet-stream",
    });

    task.on(
      "state_changed",
      (snap) => {
        const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
        if (onProgress) onProgress(pct);
      },
      (err) => reject(err),
      async () => {
        try {
          const url = await getDownloadURL(task.snapshot.ref);
          resolve({ firebasePath: path, firebaseURL: url });
        } catch (e) {
          reject(e);
        }
      }
    );
  });
};

// ─── Delete ───────────────────────────────────────────────────────────────────
/**
 * Delete a file from Firebase Storage by its path.
 * Silently ignores "object-not-found" errors.
 */
export const deleteDrawingFile = async (firebasePath) => {
  try {
    await deleteObject(ref(storage, firebasePath));
  } catch (err) {
    if (err.code !== "storage/object-not-found") {
      console.error("Firebase delete error:", err);
    }
  }
};

// ─── Invoice stamps (signature / seal) ────────────────────────────────────────
/**
 * Upload one of the images printed in the invoice signature block — the drawn
 * signature or the uploaded company seal.
 *
 * Storage path: hiland-surveyors/{adminId}/stamps/{kind}/{timestamp}.{ext}
 * A new file per save, so the invoice PDF never shows a cached older image;
 * the previous one is deleted by the caller using the returned path.
 *
 * @param {Blob|File} blob     PNG blob from the canvas, or a picked image file
 * @param {string}    adminId
 * @param {"signature"|"seal"} kind
 * @returns {Promise<{ firebasePath: string, firebaseURL: string }>}
 */
export const uploadStampFile = async (blob, adminId, kind) => {
  const type = blob.type || "image/png";
  const ext = (type.split("/")[1] || "png").replace("jpeg", "jpg").replace("svg+xml", "svg");
  const path = `hiland-surveyors/${adminId}/stamps/${kind}/${Date.now()}.${ext}`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, blob, { contentType: type });
  const url = await getDownloadURL(storageRef);
  return { firebasePath: path, firebaseURL: url };
};

export const deleteStampFile = deleteDrawingFile;

export { storage };
